"use client"

import type React from "react"

import { useState } from "react"
import { useSelector } from "react-redux"

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";

import CreateNewFolderOutlined from "@mui/icons-material/CreateNewFolderOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";

import type { TreeNode, ContextMenuState, NewItemDialogState, FolderStatus, FileStatus } from "./types/tree-types"
import { useTreeData } from "./hooks/use-tree-data"
import { useEdit } from "./hooks/use-edit"
import { useSearch } from "./hooks/use-search"
import { findNodeById } from "./utils/tree-utils"
import { TreeItem } from "./components/tree-item"
import { PropertiesPanel } from "./components/properties-panel"
import { ContextMenu } from "./components/context-menu"
import { NewItemDialog } from "./components/new-item-dialog"
import { SearchBar } from "./components/search-bar"

export default function FileTreeManager() {
  const userInfo = useSelector((state: any) => state.user)
  const {
    treeData,
    toggleExpanded,
    deleteNode,
    updateNode,
    addNode,
    updateContentsManager,
    updateUrl,
    updateStatus,
    addAttachment,
    removeAttachment,
    refreshTree,
  } = useTreeData()
  const { searchTerm, setSearchTerm, filteredData, clearSearch, isSearching } = useSearch(treeData)
  const { editingId, editingName, setEditingName, startEdit, cancelEdit } = useEdit()

  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null)
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null)
  const [newItemDialog, setNewItemDialog] = useState<NewItemDialogState>({ open: false })
  const [newItemName, setNewItemName] = useState("")
  const [renameDialog, setRenameDialog] = useState<{ open: boolean; nodeId: string | null; name: string; saving?: boolean }>({ open: false, nodeId: null, name: "", saving: false })

  // 표시할 데이터 결정 (검색 중이면 필터된 데이터, 아니면 원본 데이터)
  const displayData = isSearching ? filteredData : treeData

  const handleNodeSelect = (node: TreeNode) => {
    // 검색 중일 때는 원본 데이터에서 노드를 찾아서 선택
    if (isSearching) {
      const originalNode = findNodeById(treeData, node.id)
      setSelectedNode(originalNode)
    } else {
      setSelectedNode(node)
    }
  }

  const handleContextMenu = (event: React.MouseEvent, nodeId: string) => {
    event.preventDefault()
    const node = findNodeById(treeData, nodeId)
    if (node) {
      setSelectedNode(node)
      setContextMenu({
        mouseX: event.clientX + 2,
        mouseY: event.clientY - 6,
        nodeId,
      })
    }
  }

  const handleContextMenuClose = () => {
    setContextMenu(null)
  }

  const handleStartEdit = (id: string, currentName: string) => {
    // 다이얼로그를 통해 이름 변경
    setRenameDialog({ open: true, nodeId: id, name: currentName, saving: false })
    handleContextMenuClose()
  }

  const handleSaveEdit = () => {
    if (editingId && editingName.trim()) {
      updateNode(editingId, { title: editingName.trim() })
      if (selectedNode?.id === editingId) {
        setSelectedNode({ ...selectedNode, title: editingName.trim(), modifiedAt: new Date() })
      }
    }
    cancelEdit()
  }

  const handleRenameCancel = () => {
    setRenameDialog({ open: false, nodeId: null, name: "", saving: false })
  }

  const handleRenameSave = async () => {
    if (!renameDialog.nodeId || !renameDialog.name.trim()) return
    try {
      setRenameDialog((prev) => ({ ...prev, saving: true }))
      await updateNode(renameDialog.nodeId, { title: renameDialog.name.trim() })
      // 선택된 노드도 즉시 반영
      if (selectedNode?.id === renameDialog.nodeId) {
        setSelectedNode({ ...selectedNode, title: renameDialog.name.trim(), modifiedAt: new Date() })
      }
      setRenameDialog({ open: false, nodeId: null, name: "", saving: false })
    } catch (e) {
      setRenameDialog((prev) => ({ ...prev, saving: false }))
    }
  }

  const handleDeleteNode = (id: string) => {
    deleteNode(id)
    if (selectedNode?.id === id) {
      setSelectedNode(null)
    }
    handleContextMenuClose()
  }

  const handleAddNewItem = () => {
    if (!newItemName.trim()) return

    const newNode: TreeNode = {
      id: Date.now().toString(),
      title: newItemName.trim(),
      type: newItemDialog.type!,
      children: newItemDialog.type === "folder" ? [] : undefined,
      createdAt: new Date(),
      modifiedAt: new Date(),
      size: newItemDialog.type === "file" ? 0 : undefined,
      contentsManager: newItemDialog.type === "folder" ? [] : undefined,
      status: newItemDialog.type === "folder" ? ("draft" as FolderStatus) : ("draft" as FileStatus),
    }

    addNode(newNode, newItemDialog.parentId)
    setNewItemDialog({ open: false })
    setNewItemName("")
  }

  const handleUpdateContentsManager = (nodeId: string, contentsManager: string[]) => {
    updateContentsManager(nodeId, contentsManager)
    // 선택된 노드가 업데이트된 노드와 같다면 selectedNode도 업데이트
    if (selectedNode?.id === nodeId) {
      setSelectedNode({ ...selectedNode, contentsManager, modifiedAt: new Date() })
    }
  }

  const handleUpdateUrl = (nodeId: string, url: string) => {
    updateUrl(nodeId, url)
    // 선택된 노드가 업데이트된 노드와 같다면 selectedNode도 업데이트
    if (selectedNode?.id === nodeId) {
      setSelectedNode({ ...selectedNode, url, modifiedAt: new Date() })
    }
  }

  const handleUpdateStatus = (nodeId: string, status: FolderStatus | FileStatus) => {
    updateStatus(nodeId, status)
    setSelectedNode(null);

    // 선택된 노드가 업데이트된 노드와 같다면 selectedNode도 업데이트
    // if (selectedNode?.id === nodeId) {
    //   setSelectedNode({ ...selectedNode, status, modifiedAt: new Date() })
    // };
  }

  // 새로고침 시 선택 항목 해제하여 속성 패널 초기화
  const handleRefresh = async () => {
    setSelectedNode(null)
    await refreshTree()
  }

  const handleAddAttachment = (nodeId: string, attachment: any) => {
    addAttachment(nodeId, attachment)
    // 선택된 노드가 업데이트된 노드와 같다면 selectedNode도 업데이트
    if (selectedNode?.id === nodeId) {
      const currentAttachments = selectedNode.attachments || []
      setSelectedNode({
        ...selectedNode,
        attachments: [...currentAttachments, attachment],
        modifiedAt: new Date(),
      })
    }
  }

  const handleRemoveAttachment = (nodeId: string, attachmentId: string) => {
    removeAttachment(nodeId, attachmentId)
    // 선택된 노드가 업데이트된 노드와 같다면 selectedNode도 업데이트
    if (selectedNode?.id === nodeId && selectedNode.attachments) {
      const updatedAttachments = selectedNode.attachments.filter((att) => att.id !== attachmentId)
      setSelectedNode({
        ...selectedNode,
        attachments: updatedAttachments,
        modifiedAt: new Date(),
      })
    }
  }

  // 검색 결과 개수 계산
  const getResultCount = (nodes: TreeNode[]): number => {
    let count = 0
    const countRecursive = (nodeList: TreeNode[]) => {
      for (const node of nodeList) {
        count++
        if (node.children) {
          countRecursive(node.children)
        }
      }
    }
    countRecursive(nodes)
    return count
  }

  return (
    <Box sx={{ maxWidth: "1500px", mx: "auto", p: 1, flexGrow: 1 }}>
      <Grid container spacing={2}>
        {/* 파일 트리 영역 */}
        <Grid size={{xs:12, md:5}}>
          <Card variant="outlined" sx={{ height: "750px", display: "flex", flexDirection: "column", borderRadius: 3 }}>
            <CardHeader
              title=""
              titleTypographyProps={{ variant: "h6", fontWeight: 700 }}
              action={
                <Box sx={{ display: "flex", gap: 1 }}>
                  {userInfo?.role === 'admin' && (
                    <Tooltip title="부서 추가">
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<CreateNewFolderOutlined />}
                        onClick={() => setNewItemDialog({ open: true, type: "folder" })}
                      >
                        부서 추가
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip title="새로고침">
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<RefreshOutlined />}
                      onClick={handleRefresh}
                      style={{ paddingLeft: 8, paddingRight: 8}}
                    >
                      새로고침
                    </Button>
                  </Tooltip>
                </Box>
              }
            />

            {/* 검색창 */}
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onClearSearch={clearSearch}
              isSearching={isSearching}
              resultCount={isSearching ? getResultCount(filteredData) : undefined}
            />

            <CardContent
              sx={{ flexGrow: 1, overflow: "auto", p: 0 }}
            >
              <List dense sx={{ width: "100%", bgcolor: "background.paper", minHeight: "100%" }}>
                {displayData.length === 0 && isSearching ? (
                  <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                    <Typography variant="body2">검색 결과가 없습니다</Typography>
                    <Typography variant="caption">다른 검색어를 시도해보세요</Typography>
                  </Box>
                ) : (
                  displayData.map((node) => (
                    <TreeItem
                      key={node.id}
                      node={node}
                      level={0}
                      selectedNode={selectedNode}
                      editingId={editingId}
                      editingName={editingName}
                      treeData={treeData}
                      searchTerm={searchTerm}
                      onNodeSelect={handleNodeSelect}
                      onContextMenu={handleContextMenu}
                      onToggleExpanded={toggleExpanded}
                      onEditingNameChange={setEditingName}
                      onSaveEdit={handleSaveEdit}
                      onCancelEdit={cancelEdit}
                    />
                  ))
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* 속성 패널 영역 */}
        <Grid size={{xs:12, md:7}}>
          <Card variant="outlined" sx={{ height: "750px", display: "flex", flexDirection: "column", borderRadius: 3 }}>
            <CardContent sx={{ flexGrow: 1, overflow: "auto", p: 0 }}>
              <PropertiesPanel
                selectedNode={selectedNode}
                treeData={treeData}
                onUpdateContentsManager={handleUpdateContentsManager}
                onUpdateUrl={handleUpdateUrl}
                onUpdateStatus={handleUpdateStatus}
                onAddAttachment={handleAddAttachment}
                onRemoveAttachment={handleRemoveAttachment}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 컨텍스트 메뉴 */}
      <ContextMenu
        contextMenu={contextMenu}
        selectedNode={selectedNode}
        onClose={handleContextMenuClose}
        onNewItem={setNewItemDialog}
        onStartEdit={handleStartEdit}
        onDelete={handleDeleteNode}
      />

      {/* 새 항목 생성 다이얼로그 */}
      <NewItemDialog
        dialog={newItemDialog}
        itemName={newItemName}
        onClose={() => setNewItemDialog({ open: false })}
        onNameChange={setNewItemName}
        onCreate={handleAddNewItem}
      />

      {/* 이름 변경 다이얼로그 */}
      <Dialog open={renameDialog.open} onClose={renameDialog.saving ? undefined : handleRenameCancel} >
        <DialogTitle>이름 변경</DialogTitle>
        <DialogContent style={{width: "500px"}}>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="새 이름"
            value={renameDialog.name}
            onChange={(e) => setRenameDialog((prev) => ({ ...prev, name: e.target.value }))}
            disabled={!!renameDialog.saving}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                handleRenameSave()
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRenameCancel} disabled={!!renameDialog.saving}>취소</Button>
          <Button onClick={handleRenameSave} variant="contained" disabled={!renameDialog.name.trim() || !!renameDialog.saving}>저장</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}